import React, { useEffect, useState } from "react";
import {
  AbsoluteFill,
  Audio,
  delayRender,
  continueRender,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from "remotion";
import { ThreeCanvas } from "@remotion/three";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

export const KyuStoreReal3D: React.FC = () => {
  const { width, height, fps, durationInFrames } = useVideoConfig();
  const frame = useCurrentFrame();
  const [model, setModel] = useState<THREE.Group | null>(null);

  // Timing helper
  const sp = (delay: number, damping = 14, stiffness = 110) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 0.9 },
    });

  // Load 3D model with delayRender so headless Chromium waits
  useEffect(() => {
    const handle = delayRender("Loading 3D Character Model");
    const loader = new GLTFLoader();
    const texLoader = new THREE.TextureLoader();
    const colormap = texLoader.load(staticFile("models/Textures/colormap.png"), (t) => {
      t.flipY = false;
      t.magFilter = THREE.NearestFilter;
      t.minFilter = THREE.NearestFilter;
    });

    loader.load(
      staticFile("models/character-male-a.glb"),
      (gltf) => {
        const scene = gltf.scene;
        scene.traverse((child: any) => {
          if (child.isMesh) {
            child.material = new THREE.MeshStandardMaterial({
              map: colormap,
              roughness: 0.25,
              metalness: 0.15,
            });
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });
        setModel(scene);
        continueRender(handle);
      },
      undefined,
      () => {
        continueRender(handle);
      }
    );
  }, []);

  // Subtle 3D idle animations
  const bob = Math.sin(frame * 0.18) * 0.08;
  const rotY = Math.sin(frame * 0.06) * 0.35;
  const tiltZ = Math.sin(frame * 0.08) * 0.05;

  // Camera subtle dolly
  const camZ = interpolate(frame, [0, durationInFrames], [4.6, 4.2], {
    extrapolateRight: "clamp",
  });

  // Terminal Typing
  const fullCommand = "> /auto-inject --id=8492019 --zone=2104";
  const typedChars = Math.min(
    fullCommand.length,
    Math.floor(interpolate(frame, [45, 130], [0, fullCommand.length], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }))
  );
  const cursorBlink = Math.floor(frame / 12) % 2 === 0 ? "|" : " ";

  // Subtitles (Safe zone bottom: 400px)
  const SUBTITLES = [
    { from: 0, to: 95, text: "Pernah mikir gak, gimana diamond Kyu Store bisa masuk secepat ini?" },
    { from: 95, to: 190, text: "Begitu lu submit User ID, bot otomatis langsung verifikasi akun." },
    { from: 190, to: 280, text: "Proses QRIS real-time, inject diamond tanpa jeda admin." },
    { from: 280, to: 380, text: "Selesai dalam nol koma delapan detik. Sat-set, aman, anti-minus!" },
    { from: 380, to: 450, text: "Top-up game legal sekarang di kyustore.com!" },
  ];
  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0B0F19",
        overflow: "hidden",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* Audio Layer */}
      <Audio src={staticFile("kyustore_3d_factory_vo.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.10} />
      {frame === 10 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.6} />}
      {frame === 50 && <Audio src={staticFile("click.mp3")} volume={0.5} />}
      {frame === 170 && <Audio src={staticFile("ui-success.mp3")} volume={0.7} />}
      {frame === 270 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}

      {/* 3D WEBGL CANVAS (REAL 3D MODEL & LIGHTING) */}
      <div style={{ position: "absolute", width: "100%", height: "100%", zIndex: 10 }}>
        <ThreeCanvas
          width={width}
          height={height}
          camera={{ position: [0, 0.4, camZ], fov: 45 }}
        >
          {/* Studio Lights */}
          <ambientLight intensity={2.6} />
          <directionalLight position={[6, 12, 6]} intensity={3.8} />
          <directionalLight position={[-6, 4, -3]} intensity={2.2} color="#10B981" />
          <directionalLight position={[0, -4, 3]} intensity={1.5} color="#5D3FD3" />
          <pointLight position={[0, 2, 2]} intensity={2.0} color="#FDE68A" />

          {/* Real 3D Model Instance */}
          {model && (
            <primitive
              object={model}
              scale={[1.9, 1.9, 1.9]}
              position={[0, -0.85 + bob, 0]}
              rotation={[0.04, rotY, tiltZ]}
            />
          )}
        </ThreeCanvas>
      </div>

      {/* AMBIENT GLOW BACKDROP */}
      <div
        style={{
          position: "absolute",
          top: "22%",
          left: "20%",
          width: 640,
          height: 640,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(16, 185, 129, 0.22) 0%, rgba(93, 63, 211, 0.15) 50%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 5,
        }}
      />

      {/* 2D HUD & OVERLAYS IN FRONT OF 3D SCENE */}

      {/* Top Taped Banner */}
      <div
        style={{
          position: "absolute",
          top: 180,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 100,
        }}
      >
        <div
          style={{
            position: "relative",
            background: "#FEF3C7",
            padding: "16px 42px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.35)",
            border: "1px dashed rgba(180, 83, 9, 0.35)",
          }}
        >
          {/* Tape Left */}
          <div
            style={{
              position: "absolute",
              top: -14,
              left: -20,
              width: 55,
              height: 28,
              background: "rgba(255, 255, 255, 0.65)",
              transform: "rotate(-18deg)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          />
          {/* Tape Right */}
          <div
            style={{
              position: "absolute",
              top: -14,
              right: -20,
              width: 55,
              height: 28,
              background: "rgba(255, 255, 255, 0.65)",
              transform: "rotate(16deg)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          />

          <span
            style={{
              fontSize: 26,
              fontWeight: 900,
              color: "#78350F",
              letterSpacing: "1.5px",
              textTransform: "uppercase",
            }}
          >
            VIDEO INI DIBUAT DARI CODE (REMOTION + 3D)
          </span>
        </div>
      </div>

      {/* Neon Sign Left: VERIFIKASI ID */}
      <div
        style={{
          position: "absolute",
          top: 480,
          left: 120,
          padding: "14px 28px",
          borderRadius: 16,
          background: "rgba(16, 185, 129, 0.08)",
          border: "3px solid #10B981",
          boxShadow: "0 0 35px rgba(16, 185, 129, 0.4), inset 0 0 20px rgba(16, 185, 129, 0.2)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          zIndex: 25,
        }}
      >
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: "#10B981",
            boxShadow: "0 0 10px #10B981",
          }}
        />
        <span
          style={{
            fontSize: 26,
            fontWeight: 900,
            letterSpacing: "3px",
            color: "#A7F3D0",
            textShadow: "0 0 15px #10B981",
          }}
        >
          VERIFIKASI ID
        </span>
      </div>

      {/* Neon Sign Right: KYU STORE 3D */}
      <div
        style={{
          position: "absolute",
          top: 490,
          right: 120,
          padding: "14px 28px",
          borderRadius: 16,
          background: "rgba(255, 176, 32, 0.08)",
          border: "3px solid #FFB020",
          boxShadow: "0 0 35px rgba(255, 176, 32, 0.35)",
          zIndex: 25,
        }}
      >
        <span
          style={{
            fontSize: 26,
            fontWeight: 900,
            letterSpacing: "3px",
            color: "#FDE68A",
            textShadow: "0 0 12px #FFB020",
          }}
        >
          KYU STORE 3D
        </span>
      </div>

      {/* Floating Tag over Character */}
      <div
        style={{
          position: "absolute",
          top: 680,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#FFFFFF",
          padding: "8px 22px",
          borderRadius: 12,
          boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
          fontSize: 18,
          fontWeight: 800,
          color: "#0F172A",
          display: "flex",
          alignItems: "center",
          gap: 8,
          zIndex: 35,
        }}
      >
        <span style={{ color: "#5D3FD3" }}>●</span> 3D Mesh: character-male-a.glb
      </div>

      {/* Terminal Prompt Bar (Center Mid-Lower) */}
      <div
        style={{
          position: "absolute",
          top: 1100,
          left: "50%",
          transform: "translateX(-50%)",
          width: 740,
          padding: "16px 28px",
          borderRadius: 16,
          background: "rgba(15, 23, 42, 0.90)",
          border: "2px solid rgba(255, 255, 255, 0.12)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
          display: "flex",
          alignItems: "center",
          gap: 16,
          zIndex: 40,
        }}
      >
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#EF4444" }} />
          <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#F59E0B" }} />
          <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#10B981" }} />
        </div>
        <span
          style={{
            fontFamily: "monospace",
            fontSize: 22,
            color: "#38BDF8",
            fontWeight: 700,
            letterSpacing: "0.5px",
          }}
        >
          {fullCommand.slice(0, typedChars)}
          <span style={{ color: "#38BDF8" }}>{cursorBlink}</span>
        </span>
      </div>

      {/* Floating Metric Badge (0.8s) */}
      <div
        style={{
          position: "absolute",
          top: 1240,
          right: 140,
          transform: `scale(${sp(160)})`,
          background: "rgba(255, 255, 255, 0.96)",
          padding: "18px 28px",
          borderRadius: 20,
          boxShadow: "0 20px 45px rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          gap: 18,
          zIndex: 45,
        }}
      >
        <div
          style={{
            width: 50,
            height: 50,
            borderRadius: 16,
            background: "#ECFDF5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 26,
          }}
        >
          ⚡
        </div>
        <div>
          <div style={{ fontSize: 36, fontWeight: 900, color: "#059669", lineHeight: 1 }}>0.8s</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#64748B", marginTop: 4 }}>
            Proses Otomatis
          </div>
        </div>
      </div>

      {/* Floating Checklist Card */}
      <div
        style={{
          position: "absolute",
          top: 1240,
          left: 140,
          transform: `scale(${sp(180)})`,
          background: "rgba(255, 255, 255, 0.96)",
          padding: "18px 26px",
          borderRadius: 20,
          boxShadow: "0 20px 45px rgba(0,0,0,0.4)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          zIndex: 45,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 18, fontWeight: 800, color: "#0F172A" }}>
          <span style={{ color: "#10B981" }}>✔</span> Three.js WebGL di Remotion
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 18, fontWeight: 800, color: "#0F172A" }}>
          <span style={{ color: "#10B981" }}>✔</span> 100% Render dari Terminal
        </div>
      </div>

      {/* Subtitles (Bottom: 390px) */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 390,
            left: "50%",
            transform: "translateX(-50%)",
            width: 860,
            display: "flex",
            justifyContent: "center",
            zIndex: 100,
          }}
        >
          <div
            style={{
              background: "#F8FAFC",
              padding: "16px 32px",
              borderRadius: 16,
              boxShadow: "0 15px 35px rgba(0,0,0,0.5)",
              border: "2px solid #5D3FD3",
              textAlign: "center",
            }}
          >
            <span
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: "#0F172A",
                lineHeight: 1.4,
              }}
            >
              {currentSub.text}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
